import { useEffect, useRef, useState } from 'react';
import { ReactComponent as LoadingSvg } from '../../../../assets/svg/loading-half-status.svg';
import { ReactComponent as CheckSvg } from '../../../../assets/svg/check.svg';
import { FORMAT, formatDate } from '../../../../lib/utils';
import Title from '../../../../components/dialog/title';
import Loading from '../../../../components/loading';
import request from '../../../../lib/request';
import upload from '../../../../lib/upload';
import EditIngredient from './editIngredient';
import config from '../../../../config';

const getCurrentDateFormat = () => {
  return `${formatDate(new Date())}T${formatDate(new Date(), FORMAT.HOURMIN)}`;
};

const SaveMealPhoto = ({
  saveDialogRef,
  closeDialog,
  source,
  setSource,
  currentFile,
  setCurrentFile,
  isIngredientDialogOpen,
  setIsIngredientDialogOpen,
  user,
  openPayDialog,
  handleRefresh,
}) => {
  const scanningRef = useRef(null);
  const scanningTimeRef = useRef(0);
  const bottomDialogRef = useRef(null);
  const apiControllerRef = useRef(new AbortController());

  const [date, setDate] = useState(getCurrentDateFormat());
  const [totalCalories, setTotalCalories] = useState({
    calories: 0,
    carbohydrates: 0,
    fats: 0,
    proteins: 0,
  });
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanningStatus, setScanningStatus] = useState('Analyzing started');
  const [aiScanned, setAiScanned] = useState(false);
  const [ingredients, setIngredient] = useState([]);
  const [completeNotification, setCompleteNotification] = useState(false);
  const [credit, setCredit] = useState(0);

  useEffect(() => {
    getCredit();
  }, []);

  const getCredit = async () => {
    try {
      if (!user.activated && !user.programRegistered) {
        const { result } = await request.get('/user/free/credit');
        if (result) {
          setCredit(result.aiCredit);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const resetState = () => {
    setDate(getCurrentDateFormat());
    setTag('');
    setTotalCalories({
      calories: 0,
      carbohydrates: 0,
      fats: 0,
      proteins: 0,
    });
    setIngredient([]);
    setAiScanned(false);
    setDescription('');
    setScanningStatus('Analyzing started');
    apiControllerRef.current = new AbortController();
  };

  const handleDateChange = (e) => {
    setDate(e.target.value);
  };

  const handleDescriptionChange = (e) => {
    setDescription(String(e.target.value));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const [logDate, logTime] = date.split('T');

      let fileKey;

      if (!aiScanned) {
        fileKey = await upload('meal', currentFile);
      }

      if (date) {
        await request.post('/meal', {
          date: new Date(date).toISOString(),
          logDate,
          logTime,
          calorie: Number(totalCalories.calories),
          tag: tag ? tag.replace(/\s/g, '').split(',') : [],
          description,
          photoKey: fileKey,
          photoUrl: aiScanned ? source : undefined,
          ingredients,
        });

        handleRefresh();

        setSource(null);
        setCurrentFile(null);
        resetState();
        closeDialog();
      }
    } catch (err) {
      console.log(err);
    } finally {
      getCredit();
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isScanning) {
      apiControllerRef.current.abort('Cancelled');
    } else {
      setSource(null);
      setCurrentFile(null);
      resetState();
      closeDialog();
    }
  };

  const scanPhoto = async () => {
    setIsScanning(true);

    scanningRef.current = setInterval(() => {
      scanningTimeRef.current++;
      if (scanningTimeRef.current === 1) {
        setScanningStatus('1. Separating ingredient...');
      }
      if (scanningTimeRef.current === 7) {
        setScanningStatus('2. Calculating each portion...');
      }
      if (scanningTimeRef.current === 12) {
        setScanningStatus('3. Finalizing result...');
      }
      if (scanningTimeRef.current === 20) {
        setScanningStatus('Still processing...');
      }
      if (scanningTimeRef.current === 25) {
        setScanningStatus('Finalizing result...');
      }
      if (scanningTimeRef.current === 30) {
        setScanningStatus('Scanning failed. Please try again.');
        setTimeout(() => {
          apiControllerRef.current.abort();
        }, 1000);
      }
    }, 1000);

    try {
      const fileKey = await upload('meal', currentFile);
      setSource(`${config.resourceBaseUrl}/${fileKey}`);
      setAiScanned(true);

      const { result } = await request.post(
        '/meal/ai-scan',
        {
          photoKey: fileKey,
        },
        apiControllerRef.current.signal
      );

      setIngredient(result.ingredients);
      setTotalCalories(result.total);
    } catch (err) {
      if (err.name === 'CanceledError') {
        clearInterval(scanningRef.current);
        setScanningStatus('Analyzing started');
        setIsScanning(false);
        scanningTimeRef.current = 0;
        apiControllerRef.current = new AbortController();
        return alert('AI Scanning failed. Please try again.');
      }
      if (err.response.data.error === 'OverCreditLimit') {
        clearInterval(scanningRef.current);
        setScanningStatus('Analyzing started');
        setIsScanning(false);
        scanningTimeRef.current = 0;
        apiControllerRef.current = new AbortController();
        alert(
          'You have reached the maximum number of AI Scans. Please upgrade to PREMIUM to continue.'
        );
        openPayDialog();
        return closeDialog();
      }
    }

    clearInterval(scanningRef.current);
    setScanningStatus('Analyzing started');
    setIsScanning(false);
    scanningTimeRef.current = 0;

    setAiScanned(true);
    setCompleteNotification(true);
    setTimeout(() => {
      openBottomDialog();
      setCompleteNotification(false);
    }, 1200);
    getCredit();
  };

  const openBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.showModal();
      setIsIngredientDialogOpen(true);
    }
  };

  const closeBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.close();
      setIsIngredientDialogOpen(false);
    }
  };

  const saveIngredientList = (list, total) => {
    setIngredient(list);
    setTotalCalories(total);
    closeBottomDialog();
  };

  return (
    <dialog
      ref={saveDialogRef}
      className='p-4 w-full h-full max-w-lg max-h-full bg-primary scrollbar-hidden'
    >
      <Title
        title='Add Meal'
        onClick={handleCancel}
        color='white'
      />
      <div className='relative rounded-xl aspect-square overflow-hidden p-[2px] mb-4 w-full'>
        <div
          className={`absolute inset-0 ${isScanning ? 'borderTracer' : ''}`}
        ></div>
        <div className='relative bg-black rounded-xl overflow-hidden border border-tertiary aspect-square'>
          <img
            src={source}
            alt='meal source'
            className='w-full object-contain aspect-square'
          />
          {isScanning && (
            <div className='absolute inset-0 text-white backdrop-brightness-[20%] flex flex-col justify-center items-center font-bold rounded-xl animate-pulse'>
              <Loading size={10} />
              {scanningStatus}
            </div>
          )}
          {completeNotification && (
            <div className='absolute inset-0 text-white backdrop-brightness-[20%] flex flex-col justify-center items-center font-bold'>
              <CheckSvg className='text-accent stroke-[3] size-12' />
              Scan completed
            </div>
          )}
          <button
            className={`absolute bottom-[2px] right-[2px] rounded-tl-xl rounded-br-xl bg-gradient-to-tr from-[#fc00ff] to-[#00dbde] text-white font-bold px-4 py-2 text-sm ${
              isScanning && 'animate-pulse'
            }`}
            onClick={scanPhoto}
            disabled={isScanning}
          >
            {isScanning ? 'Scanning...' : 'AI Scan'}
            {!user.activated && !user.programRegistered && (
              <span className='text-xs'> ({credit})</span>
            )}
          </button>
        </div>
      </div>
      <div className='flex items-center justify-between gap-2 mb-4'>
        <label className='text-white font-semibold'>Date</label>
        <input
          type='datetime-local'
          max={formatDate(new Date(), FORMAT.DATEHOURMIN)}
          value={date}
          onChange={handleDateChange}
          disabled={loading}
          className='rounded-full px-2 py-1 bg-tertiary text-white'
        />
      </div>
      <div className='mb-4'>
        <div className='text-sm'>
          <div className='grid grid-cols-2 gap-1 rounded-xl text-white'>
            <div className='flex justify-between items-center border bg-tertiary/30 rounded-full p-2'>
              <div>Calories</div>
              <div>{totalCalories.calories.toFixed(1)} Cal</div>
            </div>
            <div className='flex justify-between items-center border rounded-full p-2 border-protein bg-protein/30'>
              <div>Protein</div>
              <div>{totalCalories.proteins.toFixed(1)} g</div>
            </div>
            <div className='flex justify-between items-center border rounded-full p-2 border-carbs bg-carbs/30'>
              <div>Carbs</div>
              <div>{totalCalories.carbohydrates.toFixed(1)} g</div>
            </div>
            <div className='flex justify-between items-center border rounded-full p-2 border-fats bg-fats/30'>
              <div>Fat</div>
              <div>{totalCalories.fats.toFixed(1)} g</div>
            </div>
          </div>
          <div className='flex justify-center items-center my-4 text-white'>
            <a
              className='underline cursor-pointer'
              onClick={openBottomDialog}
            >
              View ingredients
            </a>
          </div>
        </div>
        <div className='flex items-center gap-2 justify-between mb-4'>
          <label className='text-white font-semibold'>Tag</label>
          <div className='flex items-center gap-2 text-sm'>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Breakfast'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Breakfast')}
            >
              Breakfast
            </button>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Lunch'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Lunch')}
            >
              Lunch
            </button>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Dinner'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Dinner')}
            >
              Dinner
            </button>
            <button
              className={`rounded-full px-2 py-1 border ${
                tag === 'Snack'
                  ? 'border-accent bg-accent/30 text-white'
                  : 'border-dotted text-white/80 border-tertiary'
              }`}
              onClick={() => setTag('Snack')}
            >
              Snack
            </button>
          </div>
        </div>
        <div className='flex flex-col gap-2 mb-4'>
          <label className='text-white font-semibold'>Note</label>
          <textarea
            value={description}
            rows={2}
            onChange={handleDescriptionChange}
            disabled={loading}
            placeholder='Add any additional notes or details'
            className='rounded-xl p-2 bg-tertiary  text-white resize-none'
          />
        </div>
      </div>
      <div className='flex justify-between items-center mb-4'>
        <button
          className='rounded-full w-full py-2 bg-accent font-bold'
          onClick={handleSave}
          disabled={loading || isScanning}
        >
          Save
        </button>
      </div>
      {loading && (
        <div className='absolute animate-bounce top-1 flex justify-center items-center'>
          <LoadingSvg className='animate-spin' />
        </div>
      )}

      <dialog
        ref={bottomDialogRef}
        className='w-full h-full max-w-lg max-h-full bg-transparent'
        onCancel={closeBottomDialog}
      >
        <div
          className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 text-white flex flex-col border border-b-0 border-tertiary`}
        >
          <div className='text-right text-2xl mr-1'>
            <span
              onClick={closeBottomDialog}
              className='cursor-pointer'
            >
              🅧
            </span>
          </div>
          <EditIngredient
            ingredients={ingredients}
            total={totalCalories}
            save={saveIngredientList}
            isIngredientDialogOpen={isIngredientDialogOpen}
            openPayDialog={openPayDialog}
            user={user}
            credit={credit}
            getCredit={getCredit}
          />
        </div>
      </dialog>
    </dialog>
  );
};

export default SaveMealPhoto;
